import api from "PFCore/api";
import {
  CustomValueBulkResponse,
  CustomValueMergeResponse
} from "PFCore/hooks/queries/custom_fields/admin/custom_values_bulk/response";
import { _CustomValue, CustomValue, CustomValueState, CustomValueTag, MetaWithPagination } from "PFTypes";
import { Id } from "PFTypes/id";
import { QueryParams } from "PFTypes/query_params";

export type CreateCustomValuePayload = {
  value: string;
  ordinal?: number;
};

export type CustomValuesByTypeResponse = {
  entries: CustomValue[];
  meta: MetaWithPagination & {
    counters: {
      state_tally: Record<CustomValueState, number>;
    };
  };
};

export const fetchCustomValuesByType = (customTypeId: Id, params: QueryParams) => {
  const { page = 1, perPage = 30, ...restParams } = params;

  return api<CustomValuesByTypeResponse>({
    url: `admin/custom_types/${customTypeId}/custom_values`,
    params: { page, perPage, ...restParams }
  });
};

export const fetchCustomValue = (customTypeId: Id, customValueId: Id) =>
  api<_CustomValue>({
    url: `admin/custom_types/${customTypeId}/custom_values/${customValueId}`,
    method: "GET"
  });

export const createCustomValueByType = (
  customTypeId: Id,
  data: CreateCustomValuePayload
): Promise<CustomValue> =>
  api({
    url: `admin/custom_types/${customTypeId}/custom_values`,
    method: "POST",
    data
  });

export const updateCustomValue = (
  customTypeId: Id,
  customValueId: Id,
  data: CreateCustomValuePayload
): Promise<CustomValue> =>
  api({
    url: `admin/custom_types/${customTypeId}/custom_values/${customValueId}`,
    method: "PUT",
    data
  });

export const approveCustomValuesByType = (customTypeId: Id, ids: Id[]): Promise<CustomValueBulkResponse> =>
  api({
    url: `admin/custom_types/${customTypeId}/custom_values/bulk/approve`,
    method: "PUT",
    data: { ids }
  });

export const blacklistCustomValuesByType = (customTypeId: Id, ids: Id[]): Promise<CustomValueBulkResponse> =>
  api({
    url: `admin/custom_types/${customTypeId}/custom_values/bulk/block`,
    method: "PUT",
    data: { ids }
  });

export const deleteCustomValuesByType = (customTypeId: Id, ids: Id[]) =>
  api({
    url: `admin/custom_types/${customTypeId}/custom_values/bulk/delete`,
    method: "PUT",
    data: { ids }
  });

export const mergeCustomValuesByType = (
  customTypeId: Id,
  targetCustomValueId: Id,
  ids: Id[]
): Promise<CustomValueMergeResponse> =>
  api({
    url: `admin/custom_types/${customTypeId}/custom_values/bulk/merge`,
    method: "PUT",
    data: {
      ids,
      event: "merge",
      parameters: { targetCustomValueId }
    }
  });

export const exportCustomValuesByType = (customTypeId: Id, ids: Id[]): Promise<string> =>
  api({
    url: `admin/custom_types/${customTypeId}/custom_values/export.csv`,
    method: "POST",
    data: { filters: { fields: { id: ids } } }
  });

export const resetCustomValueChangeByType = (customTypeId: Id, ids: Id[]): Promise<CustomValueBulkResponse> =>
  api({
    url: `admin/custom_types/${customTypeId}/custom_values/bulk/reset`,
    method: "PUT",
    data: { ids }
  });

export const fetchCustomValuesTags = (): Promise<CustomValueTag[]> =>
  api({
    url: "admin/custom_values/tags"
  });

export type CreateCustomTagsParams = {
  name?: string;
  displayAs?: string;
  iconName?: string;
};

export const createCustomValuesTag = (payload: CreateCustomTagsParams) =>
  api({
    url: "admin/custom_values/tags",
    method: "POST",
    data: payload
  });

export const editCustomValuesTag = (id: Id, payload: CreateCustomTagsParams) =>
  api({
    url: `admin/custom_values/tags/${id}`,
    method: "PUT",
    data: payload
  });

export const removeCustomValuesTag = (id: Id) =>
  api({
    url: `admin/custom_values/tags/${id}`,
    method: "DELETE"
  });

export const setOrdinalCustomValues = (customTypeId: number, customValuesIds: number[]) =>
  api({
    url: `admin/custom_types/${customTypeId}/custom_values/set_order`,
    method: "PUT",
    data: {
      customValueIds: customValuesIds
    }
  });

export const fetchUnusedCustomValues = (customTypeId: Id, perPage: number = 25) =>
  api({
    url: `admin/custom_types/${customTypeId}/custom_values/unused`,
    method: "GET",
    params: {
      perPage
    }
  });

export const cleanupUnusedCustomValues = (customTypeId: Id): Promise<void> =>
  api({
    url: `admin/custom_types/${customTypeId}/custom_values/cleanup_unused`,
    method: "POST"
  });

export const cleanupCustomValues = (customTypeId: number): Promise<void> =>
  api({
    url: `admin/custom_types/${customTypeId}/custom_values/cleanup`,
    method: "PUT"
  });
